/*
  Venture Medical dotted waves
  ------------------------------------------------------------
  Ported from the "Venture Medical Waves v5" Canvas2D embed on venturemedical.com.
  Draws 60 perspective-projected dotted sine waves on a transparent canvas, so the
  section's own color-scheme background shows through.

  Markup (see snippets/venture-waves.liquid):
    <div class="venture-waves" data-wave-color="cream|yellow|white|blue" data-wave-blend="additive|normal"></div>

  Behaviour:
    - pauses when the element is off screen (IntersectionObserver)
    - draws a single static frame when the visitor prefers reduced motion
    - re-initialises sections reloaded by the Shopify theme editor
*/
(function () {
  'use strict';

  var DOTS = {
    cream:  [245, 244, 235],
    yellow: [243, 176, 62],
    white:  [255, 255, 255],
    blue:   [18, 0, 107]
  };

  // Fixed camera, identical to the main site
  var CAM = [-3, 1.5, 5];
  var HALF_TAN = Math.tan(55 * Math.PI / 360);
  var fl = Math.sqrt(9 + 2.25 + 25);
  var F = [3 / fl, -1.5 / fl, -5 / fl];
  var rr = [-F[2], 0, F[0]];
  var rl = Math.sqrt(rr[0] * rr[0] + rr[2] * rr[2]);
  var R = [rr[0] / rl, 0, rr[2] / rl];
  var U = [
    R[1] * F[2] - R[2] * F[1],
    R[2] * F[0] - R[0] * F[2],
    R[0] * F[1] - R[1] * F[0]
  ];

  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function init(el) {
    if (el.__vmWaves) return;
    el.__vmWaves = true;

    var dot = DOTS[el.getAttribute('data-wave-color')] || DOTS.cream;
    var additive = el.getAttribute('data-wave-blend') !== 'normal';

    var WC = 60, PPW = 180, AMP = 0.6, LS = 0.7, SPD = 0.012;
    var canvas = document.createElement('canvas');
    el.appendChild(canvas);
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var dotColor = 'rgb(' + dot[0] + ',' + dot[1] + ',' + dot[2] + ')';

    var waves = [];
    for (var i = 0; i < WC; i++) {
      var z = i * LS - 10 + (Math.random() - 0.5) * 0.25;
      var dist = Math.abs(z - 5);
      var op = 0.4;
      if (dist > 3) op = Math.max(0.05, 0.4 - (dist - 3) * 0.05);
      else if (z > 3) op = Math.max(0.08, 0.4 - (3 - dist) * 0.15);
      waves.push({
        z: z, opacity: op,
        amp: AMP * (0.6 + Math.random() * 0.8),
        f1: 0.12 + Math.random() * 0.08,
        f2: 0.05 + Math.random() * 0.06,
        speed: 0.6 + Math.random() * 0.8,
        phase: Math.random() * Math.PI * 2,
        vari: 0.05 + (Math.abs(z) / 20) * 0.3,
        yOff: Math.sin(i * 0.1) * 0.1
      });
    }
    waves.sort(function (a, b) { return a.z - b.z; });
    var waveProj = [];
    for (var k = 0; k < waves.length; k++) {
      var dz = waves[k].z - CAM[2];
      waveProj.push({ dzF: dz * F[2], dzR: dz * R[2], dzU: dz * U[2] });
    }

    var w = 0, h = 0, t = 0, run = false, raf = 0;

    function resize() {
      w = el.offsetWidth;
      h = el.offsetHeight;
      if (!w || !h) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      if (reduceMotion) frame();
    }

    function frame() {
      var cw = w * dpr, ch = h * dpr;
      if (!cw || !ch) return;
      var hw = cw / 2, hh = ch / 2;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, cw, ch);
      if (additive) ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = dotColor;
      for (var wi = 0; wi < waves.length; wi++) {
        var wave = waves[wi];
        var wp = waveProj[wi];
        var tSpeed = t * wave.speed;
        var tSpeed2 = tSpeed * 0.7 + wave.phase;
        ctx.globalAlpha = wave.opacity;
        ctx.beginPath();
        for (var j = 0; j < PPW; j++) {
          var xW = (j / PPW) * 16 - 8;
          var yW = Math.sin(j * wave.f1 + tSpeed) * wave.amp
                 + Math.sin(j * wave.f2 + tSpeed2) * (wave.amp * wave.vari)
                 + wave.yOff;
          var dx = xW - CAM[0], dy = yW - CAM[1];
          var depth = dx * F[0] + dy * F[1] + wp.dzF;
          if (depth <= 0.1) continue;
          var invD = 1 / (depth * HALF_TAN);
          var sx = hw + (dx * R[0] + dy * R[1] + wp.dzR) * invD * hh;
          var sy = hh - (dx * U[0] + dy * U[1] + wp.dzU) * invD * hh;
          var dotR = 0.0075 * invD * hh;
          if (dotR < 0.3) continue;
          ctx.moveTo(sx + dotR, sy);
          ctx.arc(sx, sy, dotR, 0, 6.2832);
        }
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }

    function loop() {
      if (!run) return;
      if (!el.isConnected) { stop(); return; }
      raf = requestAnimationFrame(loop);
      t += SPD;
      frame();
    }

    function start() {
      if (run || reduceMotion) return;
      run = true;
      loop();
    }

    function stop() {
      run = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    resize();
    window.addEventListener('resize', resize);
    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(el);

    if (reduceMotion) {
      t = 2; // a pleasant static phase
      frame();
      return;
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { en.isIntersecting ? start() : stop(); });
      }, { threshold: 0 }).observe(el);
    } else {
      start();
    }
  }

  function boot(root) {
    var els = (root || document).querySelectorAll('[data-wave-color]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  // Shopify theme editor re-renders sections; pick up any new wave containers.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
